/* eslint-disable complexity */
import { CONSC_LABELS } from "@/resources/constants";
import { obsMetricNames, obsMetricToLabel } from "@/resources/observations_metricUtils";
import { Observation } from "@/resources/types";
import { ObsTabs } from "@/components/ObservationChart/ObservationTabs_ctx";
import { DetailProps } from "./ObservationSummary";
import { get } from "lodash";

// Observation data that can be displayed in a vitals signs graph
export const obsKeysForVitalSignsGraph = [
  obsMetricNames.respirations,
  obsMetricNames.o2Sats,
  obsMetricNames.o2Flow,
  obsMetricNames.heartRate,
  obsMetricNames.temperature,
  obsMetricNames.bloodGlucose,

] as const;

// Observation data that can be displayed in the neuro graph
export const obsKeysForNeurologicalGraph = [
  obsMetricNames.gcsTitle,
  obsMetricNames.gcsEyes,
  obsMetricNames.gcsVerbal,
  obsMetricNames.gcsMovement,
  obsMetricNames.gcsTotal,
  //#
  obsMetricNames.limbMovTitle,
  obsMetricNames.limbMovRightArm,
  obsMetricNames.limbMovLeftArm,
  obsMetricNames.limbMovRightLeg,
  obsMetricNames.limbMovLeftLeg,
  //#
  obsMetricNames.pupilsTitle,

  obsMetricNames.pupilLeftTitle,
  obsMetricNames.pupilLeftSize,
  obsMetricNames.pupilLeftReaction,

  obsMetricNames.pupilRightTitle,
  obsMetricNames.pupilRightSize,
  obsMetricNames.pupilRightReaction,
  //#
  obsMetricNames.limbNeuroStatusTitle,

  obsMetricNames.limbNeuroRightArmTitle,
  obsMetricNames.limbNeuroRightArmColour,
  obsMetricNames.limbNeuroRightArmWarmth,
  obsMetricNames.limbNeuroRightArmSensation,
  obsMetricNames.limbNeuroRightArmCapillaryRefill,
  obsMetricNames.limbNeuroRightArmPulse,
  obsMetricNames.limbNeuroRightArmMovement,

  obsMetricNames.limbNeuroLeftArmTitle,
  obsMetricNames.limbNeuroLeftArmColour,
  obsMetricNames.limbNeuroLeftArmWarmth,
  obsMetricNames.limbNeuroLeftArmSensation,
  obsMetricNames.limbNeuroLeftArmCapillaryRefill,
  obsMetricNames.limbNeuroLeftArmPulse,
  obsMetricNames.limbNeuroLeftArmMovement,

  obsMetricNames.limbNeuroRightLegTitle,
  obsMetricNames.limbNeuroRightLegColour,
  obsMetricNames.limbNeuroRightLegWarmth,
  obsMetricNames.limbNeuroRightLegSensation,
  obsMetricNames.limbNeuroRightLegCapillaryRefill,
  obsMetricNames.limbNeuroRightLegPulse,
  obsMetricNames.limbNeuroRightLegMovement,

  obsMetricNames.limbNeuroLeftLegTitle,
  obsMetricNames.limbNeuroLeftLegColour,
  obsMetricNames.limbNeuroLeftLegWarmth,
  obsMetricNames.limbNeuroLeftLegSensation,
  obsMetricNames.limbNeuroLeftLegCapillaryRefill,
  obsMetricNames.limbNeuroLeftLegPulse,
  obsMetricNames.limbNeuroLeftLegMovement,
] as const;

// Observation data that can be displayed in a bedside graph
export const obsKeysForBedsideGraph = [
  obsMetricNames.bedsideBloodPressureLaying,
  obsMetricNames.bedsideBloodPressureLeftArm,
  obsMetricNames.bedsideBloodPressureRightArm,
  obsMetricNames.bedsideBloodPressureStanding,
  obsMetricNames.bedsideHeartRate,
] as const;

// Observation data that can be displayed in a ventilator graph
export const obsKeysForVentilatorGraph = [
  obsMetricNames.ventilatorEndTidalCarbonDioxide,
  obsMetricNames.ventilatorFrequencyRate,
  obsMetricNames.ventilatorMinuteVentilation,
  obsMetricNames.ventilatorPeakPressure,
  obsMetricNames.ventilatorTidalVolume,
] as const;

// Observation data that can be displayed in a ventilator graph
export const obsKeysForUrinalysisGraph = [
  obsMetricNames.urinalysisAbnormalities,
  obsMetricNames.urinalysisDistream,
  obsMetricNames.urinalysisPregnancy,
  obsMetricNames.urinalysisGlucose,
  obsMetricNames.urinalysisProtein,
  obsMetricNames.urinalysisBlood,
  obsMetricNames.urinalysisBilirubin,
  obsMetricNames.urinalysisNitrite,
  obsMetricNames.urinalysisUrobilinogen,
  obsMetricNames.urinalysisLeukocytes,
  obsMetricNames.urinalysisPh,
  obsMetricNames.urinalysisKetone,
  obsMetricNames.urinalysisSpecificGravity
] as const;

export type GetValueFromObsProps = {
  observation: Observation,
  key: typeof obsKeysForVitalSignsGraph[number],
  type: ObsTabs.VITALS_SIGNS
} | {
  observation: Observation,
  key: typeof obsKeysForNeurologicalGraph[number],
  type: ObsTabs.NEUROLOGICAL,
} | {
  observation: Observation,
  key: typeof obsKeysForBedsideGraph[number],
  type: ObsTabs.BEDSIDE,
} | {
  observation: Observation,
  key: typeof obsKeysForVentilatorGraph[number],
  type: ObsTabs.VENTILATOR
} | {
  observation: Observation,
  key: typeof obsKeysForUrinalysisGraph[number],
  type: ObsTabs.URINALYSIS
}
export const getValueFromObs = (props: GetValueFromObsProps) => {
  const { observation, key, type } = props;
  switch (type) {
    case ObsTabs.BEDSIDE:
      switch (key) {
        case "bedsideBloodPressureLaying": return {
          systolic: observation.bedside?.bloodPressure?.laying?.systolic,
          diastolic: observation.bedside?.bloodPressure?.laying?.diastolic,
        };
        case "bedsideBloodPressureLeftArm": return {
          systolic: observation.bedside?.bloodPressure?.leftArm?.systolic,
          diastolic: observation.bedside?.bloodPressure?.leftArm?.diastolic,
        };
        case "bedsideBloodPressureRightArm": return {
          systolic: observation.bedside?.bloodPressure?.rightArm?.systolic,
          diastolic: observation.bedside?.bloodPressure?.rightArm?.diastolic,
        };
        case "bedsideBloodPressureStanding": return {
          systolic: observation.bedside?.bloodPressure?.standing?.systolic,
          diastolic: observation.bedside?.bloodPressure?.standing?.diastolic,
        };
        case "bedsideHeartRate": return observation.bedside?.heartRate;
      }
      break;
    case ObsTabs.VENTILATOR:
      switch (key) {
        case "ventilatorEndTidalCarbonDioxide": return observation.ventilator?.endTidalCarbonDioxide;
        case "ventilatorFrequencyRate": return observation.ventilator?.frequencyRate;
        case "ventilatorMinuteVentilation": return observation.ventilator?.minuteVentilation;
        case "ventilatorPeakPressure": return observation.ventilator?.peakPressure;
        case "ventilatorTidalVolume": return observation.ventilator?.tidalVolume;
      }
      break;
    case ObsTabs.URINALYSIS:
      switch (key) {
        case "urinalysisAbnormalities": return observation.urinalysis?.abnormalities;
        case "urinalysisDistream": return observation.urinalysis?.distream;
        case "urinalysisPregnancy": return observation.urinalysis?.pregnancy;
        case "urinalysisGlucose": return observation.urinalysis?.glucose;
        case "urinalysisProtein": return observation.urinalysis?.protein;
        case "urinalysisBlood": return observation.urinalysis?.blood;
        case "urinalysisBilirubin": return observation.urinalysis?.bilirubin;
        case "urinalysisNitrite": return observation.urinalysis?.nitrite;
        case "urinalysisUrobilinogen": return observation.urinalysis?.urobilinogen;
        case "urinalysisLeukocytes": return observation.urinalysis?.leukocytes;
        case "urinalysisPh": return observation.urinalysis?.ph;
        case "urinalysisKetone": return observation.urinalysis?.ketone;
        case "urinalysisSpecificGravity": return observation.urinalysis?.specificGravity;
      }
      break;
    case ObsTabs.VITALS_SIGNS:
    default:
      return get(observation, key);
  }
};

type MakeAllGraphDataProps = {
  observations: Observation[],
  type: ObsTabs
}

/** for graph plotting */
const getKeysForGraphByType = (type: ObsTabs) => {
  return (
    {
      [ObsTabs.VITALS_SIGNS]: obsKeysForVitalSignsGraph,
      [ObsTabs.BEDSIDE]: obsKeysForBedsideGraph,
      [ObsTabs.VENTILATOR]: obsKeysForVentilatorGraph,
      [ObsTabs.URINALYSIS]: obsKeysForUrinalysisGraph,
      [ObsTabs.NEUROLOGICAL]: [],
    }[type]);
};

// Generate graph data for each of the keys defined directly above
export const makeAllGraphData = ({ observations, type }: MakeAllGraphDataProps) => {
  const keysForGraph = getKeysForGraphByType(type);
  const allData: { [P in (typeof keysForGraph)[number]]?: number[] } = {};

  observations.forEach((observation) => {
    keysForGraph.forEach((key) => {
      let value = getValueFromObs({ observation, type, key } as GetValueFromObsProps);

      if (value === undefined || (typeof value === "object" && !value.systolic)) {
        return;
      }
      if (typeof value === "object" && value.systolic) {
        value = value.systolic;
      }

      const data = allData[key];
      allData[key] = data === undefined ? [Number(value)] : [...data, Number(value)];
    });
  });
  return allData;
};

type GetItemsForSummary = {
  showGraphs: boolean,
} & MakeAllGraphDataProps;

// TODO: This is not working for urinalysis. Probably because urinalysis
// data is nested in observation.urinalysis.
export const getItemsForSummary = (props: GetItemsForSummary) => {
  const { observations, showGraphs, type } = props;
  // Combine all observations into a summary that contains the last known
  // value of each observation type (i.e. last known temperature, last known
  // heart rate, etc.)
  const summary = [...observations].reduce(
    (summaryData, currentObs) => ({ ...summaryData, ...currentObs }),
    {} as Observation
  );

  const { bloodPressure, consciousness } = summary;
  const keysForGraph = getKeysForGraphByType(type);
  const graphData = showGraphs ? makeAllGraphData({ observations, keysForGraph, type } as MakeAllGraphDataProps) : {};
  let items: Omit<DetailProps, "mini" | "showChartLineColors">[] = [];

  switch (type) {
    case ObsTabs.VITALS_SIGNS:
      items = [
        {
          label: obsMetricToLabel("respirations"),
          value: summary.respirations,
          units: "bpm",
          graphData: graphData.respirations,
        },
        {
          label: obsMetricToLabel("o2Sats"),
          value: summary.o2Sats,
          units: "%",
          graphData: graphData.o2Sats,
        },
        {
          label: obsMetricToLabel("o2Flow"),
          value: summary.o2Flow,
          units: "L/min",
          graphData: graphData.o2Flow,
        },
        {
          label: obsMetricToLabel("heartRate"),
          value: summary.heartRate,
          units: "bpm",
          graphData: graphData.heartRate,
        },
        {
          label: obsMetricToLabel("bloodPressure"),
          value: bloodPressure && `${bloodPressure.systolic}/${bloodPressure.diastolic}`,
          units: "mmHg",
        },
        {
          label: obsMetricToLabel("temperature"),
          value: summary.temperature,
          units: "°C",
          graphData: graphData.temperature,
        },
        {
          label: obsMetricToLabel("consciousness"),
          value: consciousness && CONSC_LABELS[consciousness],
        },
        {
          label: obsMetricToLabel("bloodGlucose"),
          value: summary.bloodGlucose,
          units: "mmol/L",
          graphData: graphData.bloodGlucose,
        },
      ];
      break;
    case ObsTabs.BEDSIDE:
      items = [
        {
          label: obsMetricToLabel("bedsideBloodPressureLeftArm"),
          value: summary.bedside?.bloodPressure?.leftArm && `${summary.bedside?.bloodPressure?.leftArm.systolic}/${summary.bedside?.bloodPressure?.leftArm.diastolic}`,
          units: "mmHg",
          graphData: graphData.bedsideBloodPressureLeftArm,
        },
        {
          label: obsMetricToLabel("bedsideBloodPressureRightArm"),
          value: summary.bedside?.bloodPressure?.rightArm && `${summary.bedside?.bloodPressure?.rightArm.systolic}/${summary.bedside?.bloodPressure?.rightArm.diastolic}`,
          units: "mmHg",
          graphData: graphData.bedsideBloodPressureRightArm,
        },
        {
          label: obsMetricToLabel("bedsideBloodPressureLaying"),
          value: summary.bedside?.bloodPressure?.laying && `${summary.bedside?.bloodPressure?.laying.systolic}/${summary.bedside?.bloodPressure?.laying.diastolic}`,
          units: "mmHg",
          graphData: graphData.bedsideBloodPressureLaying,
        },
        {
          label: obsMetricToLabel("bedsideBloodPressureStanding"),
          value: summary.bedside?.bloodPressure?.standing && `${summary.bedside?.bloodPressure?.standing.systolic}/${summary.bedside?.bloodPressure?.standing.diastolic}`,
          units: "mmHg",
          graphData: graphData.bedsideBloodPressureStanding,
        },
        {
          label: obsMetricToLabel("bedsideHeartRate"),
          value: summary.bedside?.heartRate,
          units: "bpm",
          graphData: graphData.bedsideHeartRate,
        },
      ];
      break;
    case ObsTabs.VENTILATOR:
      items = [
        {
          label: obsMetricToLabel("ventilatorTidalVolume"),
          value: summary.ventilator?.tidalVolume,
          units: "ml",
          graphData: graphData.ventilatorTidalVolume,
        },
        {
          label: obsMetricToLabel("ventilatorMinuteVentilation"),
          value: summary.ventilator?.minuteVentilation,
          units: "L/min",
          graphData: graphData.ventilatorMinuteVentilation,
        },
        {
          label: obsMetricToLabel("ventilatorFrequencyRate"),
          value: summary.ventilator?.frequencyRate,
          units: "bpm",
          graphData: graphData.ventilatorFrequencyRate,
        },
        {
          label: obsMetricToLabel("ventilatorPeakPressure"),
          value: summary.ventilator?.peakPressure,
          units: "cmH2O",
          graphData: graphData.ventilatorPeakPressure,
        },
        {
          label: obsMetricToLabel("ventilatorEndTidalCarbonDioxide"),
          value: summary.ventilator?.endTidalCarbonDioxide,
          units: "mmHg",
          graphData: graphData.ventilatorEndTidalCarbonDioxide,
        },
      ];
      break;
    case ObsTabs.URINALYSIS:
      items = [
        {
          label: obsMetricToLabel("urinalysisAbnormalities"),
          value: summary.urinalysis?.abnormalities,
          units: "",
        },
        {
          label: obsMetricToLabel("urinalysisLeukocytes"),
          value: summary.urinalysis?.leukocytes,
          units: "",
        },
        {
          label: obsMetricToLabel("urinalysisPregnancy"),
          value: summary.urinalysis?.pregnancy,
          units: "",
        },
      ];
      break;
  }

  return items;
};
